import {
  put, takeLatest, all, fork, join, select,
} from 'redux-saga/effects';

import i18n from '../../../../i18n/i18n';
import { copyToClipboard } from '@utils/clipboard';
import { COURSE } from '@shared/Resources/types';
import { setOverview } from '@containers/Admin/shared/ResourcesWrapper/actions';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import {
  putUserPageLoadingStatus,
  setPhoneUpdateLoading,
  finishPhoneUpdate,
  showUserPageError,
  USER_PAGE_ACTIONS,
  putUserPage,
  showToast,
  fetchUserPage,
  putUserPageMentors,
  putUserPageBusinessUnits,
  fetchUserPersonalProgram,
} from './actions';
import {
  sendUserCourse,
  setUserMentors,
  sendUserBusinessUnits,
  sendUserStatus,
  moveUserToCourseLevel,
  setUserCourseLocationFlag,
  clearUserCourseLocationFlag,
  getAllMentors,
  getUserInformation,
  getUserPersonalProgram,
  adminResetPassword,
  sendUserLocale,
  sendUserExperimentalFeatures,
} from './services';
import {
  adminUpdateUserBirthdate,
  adminUpdateUserMfa,
  adminUpdateUserName,
  adminUpdateUserPhone,
} from '@services/adminUserAuthService';
import { AUTH_BLOCKED_REASONS, TOAST_TYPES } from './consts';
import { formatMentorsToOptions } from './helpers';
import { getAllBusinessUnits } from '@containers/Admin/shared/businessUnitService';

export function* callFetchOneUser(action) {
  try {
    const id = action.payload;
    yield put(putUserPageLoadingStatus(true));
    const currentUserRoles = yield select((state) => state.getIn(['app', 'authentication', 'user', 'roles']).toJS());
    const isTechAdmin = Array.isArray(currentUserRoles) && currentUserRoles.includes('40');
    const isAdmin = Array.isArray(currentUserRoles)
      && (currentUserRoles.includes('30') || currentUserRoles.includes('40') || currentUserRoles.includes('41'));

    const res = yield fork(getUserInformation, id);
    const mentorsData = isAdmin ? yield fork(getAllMentors) : null;
    const businessUnitsData = isTechAdmin ? yield fork(getAllBusinessUnits) : null;

    const { userData, errors } = yield join(res);
    const { allMentors, errors: mentorsErrors } = mentorsData ? yield join(mentorsData) : { allMentors: [], errors: null };
    const allBusinessUnitsResult = businessUnitsData ? yield join(businessUnitsData) : { allBusinessUnits: [] };
    const { allBusinessUnits, errors: businessUnitsErrors } = allBusinessUnitsResult;

    if (errors || mentorsErrors || businessUnitsErrors) {
      yield put(showUserPageError());
    } else {
      yield put(putUserPageMentors(formatMentorsToOptions(allMentors)));
      yield put(putUserPageBusinessUnits(allBusinessUnits));
      yield put(putUserPage(userData));
    }
  } catch {
    yield put(showUserPageError());
  } finally {
    yield put(putUserPageLoadingStatus(false));
  }
}

export function* callAttachUserPageCourse({ payload: { courseId, userId } }) {
  try {
    const { userData } = yield select((state) => state.get('userPage').toJS());
    const res = yield fork(sendUserCourse, courseId, userId);
    const { errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
    } else {
      yield put(fetchUserPage(userData.id));
      yield put(fetchUserPersonalProgram(userId));
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_ATTACHED));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callChangeUserBusinessUnits({ payload: { businessUnits, userId } }) {
  try {
    const { userData } = yield select((state) => state.get('userPage').toJS());
    const res = yield fork(sendUserBusinessUnits, userId, businessUnits);
    const { errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
    } else {
      yield put(fetchUserPage(userData.id));
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_ATTACHED));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callAttachUserPageMentorsBulk({ payload: { userId, changes } }) {
  try {
    const { userData } = yield select((state) => state.get('userPage').toJS());

    if (!Array.isArray(changes) || changes.length === 0) {
      return;
    }

    const { userMentors } = yield select((state) => state.get('userPage').toJS());
    const currentMentorIds = Array.isArray(userMentors)
      ? userMentors.map(({ value }) => value)
      : [];

    const toAdd = changes
      .filter(({ isRemove }) => !isRemove)
      .map(({ mentorId }) => mentorId);
    const toRemove = new Set(changes
      .filter(({ isRemove }) => isRemove)
      .map(({ mentorId }) => mentorId));

    const finalMentorIds = Array.from(
      new Set([
        ...currentMentorIds.filter((mentorId) => !toRemove.has(mentorId)),
        ...toAdd,
      ]),
    );

    const res = yield fork(setUserMentors, userId, finalMentorIds);
    const { errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
    } else {
      yield put(fetchUserPage(userData.id));
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_ATTACHED));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callMoveUserToCourseLevel({ payload }) {
  try {
    const res = yield fork(moveUserToCourseLevel, payload);
    const { data, errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_CHANGED_LEVEL));
    } else {
      const overview = yield select((state) => state.getIn(['resourceManager', 'overview', 'resource'])?.toJS());
      const nextCourseActivity = data?.moveUserToCourseActivity;
      if (overview && nextCourseActivity) {
        yield put(setOverview(COURSE, { ...overview, nextCourseActivity }, null));
      }
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_CHANGED_LEVEL));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_CHANGED_LEVEL));
  }
}

function* callSetUserCourseLocationFlag({ payload }) {
  try {
    const res = yield fork(setUserCourseLocationFlag, payload);
    const result = yield join(res);
    const flaggedCourseLocation = result?.data?.setUserCourseLocationFlag;
    if (!flaggedCourseLocation || result.errors) {
      yield put(showUserPageError());
      return;
    }
    const overview = yield select((state) => state.getIn(['resourceManager', 'overview', 'resource'])?.toJS());
    if (overview) {
      yield put(setOverview(COURSE, { ...overview, flaggedCourseLocation }, null));
    }
  } catch {
    yield put(showUserPageError());
  }
}

function* callClearUserCourseLocationFlag({ payload: { userId } }) {
  try {
    const res = yield fork(clearUserCourseLocationFlag, userId);
    const result = yield join(res);
    if (!result?.data?.clearUserCourseLocationFlag || result.errors) {
      yield put(showUserPageError());
      return;
    }
    const overview = yield select((state) => state.getIn(['resourceManager', 'overview', 'resource'])?.toJS());
    if (overview) {
      yield put(setOverview(COURSE, { ...overview, flaggedCourseLocation: null }, null));
    }
  } catch {
    yield put(showUserPageError());
  }
}

export function* callChangeUserStatus({ payload: { userId, status } }) {
  try {
    const { userData } = yield select((state) => state.get('userPage').toJS());
    const res = yield fork(sendUserStatus, userId, status);
    const result = yield join(res);

    if (!result || result.errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
      return;
    }

    const cognitoUserMissing = Boolean(
      result?.data?.createUserState?.cognitoUserMissing,
    );

    yield put(fetchUserPage(userData.id));
    yield put(
      showToast(
        cognitoUserMissing
          ? TOAST_TYPES.SHOW_WARNING_USER_NOT_IN_COGNITO
          : TOAST_TYPES.SHOW_SUCCESS_UPDATED,
      ),
    );
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callChangeUserLocale({ payload: { userId, locale } }) {
  try {
    const { userData } = yield select((state) => state.get('userPage').toJS());
    const res = yield fork(sendUserLocale, userId, locale);
    const { errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
    } else {
      yield put(fetchUserPage(userData.id));
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_UPDATED));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callChangeUserExperimentalFeatures({ payload: { userId, experimentalFeatures } }) {
  try {
    const res = yield fork(sendUserExperimentalFeatures, userId, experimentalFeatures);
    const { errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
    } else {
      yield put(fetchUserPage(userId));
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_UPDATED));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callFetchUserPersonalProgram({ payload }) {
  try {
    yield put(putUserPageLoadingStatus(true));

    const res = yield fork(getUserPersonalProgram, payload);
    const { entityCoursesForUser, userData, errors } = yield join(res);

    if (errors) {
      yield put(showUserPageError());
    } else {
      const course = entityCoursesForUser.length > 0 && entityCoursesForUser[0];
      yield put(setOverview(COURSE, course && {
        ...course,
        nextCourseActivity: userData && userData.nextCourseActivity,
        flaggedCourseLocation: userData && userData.flaggedCourseLocation,
      }, null));
    }
  } catch {
    yield put(showUserPageError());
  } finally {
    yield put(putUserPageLoadingStatus(false));
  }
}

// Saga to handle admin password reset
export function* callResetUserPassword({ payload: { userId } }) {
  try {
    yield put(putUserPageLoadingStatus(true));

    const { userData } = yield select((state) => state.get('userPage').toJS());
    const userEmail = userData?.email || userData?.userName || '';

    const result = yield adminResetPassword(userId);
    const blockedReason = result?.blockedReason;
    const temporaryPassword = result?.temporaryPassword;
    const emailSent = result?.emailSent;

    // Backward-compatible guard:
    // Treat nulls as "inactive blocked" even if the client didn't request blockedReason.
    const isBlockedInactive =
      blockedReason === AUTH_BLOCKED_REASONS.USER_INACTIVE ||
      (temporaryPassword === null && emailSent === null);
    if (isBlockedInactive) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ACTIVATE_USER_FIRST));
      return;
    }

    if (!temporaryPassword) throw new Error('Missing temporary password');

    const deliveryBody = emailSent === false
      ? i18n.t('Authentication.Password.adminTempPasswordEmailFailed', {
        email: userEmail,
      })
      : i18n.t('Authentication.Password.adminTempPasswordEmailSent', {
        email: userEmail,
      });

    const handleCopy = () => {
      copyToClipboard(temporaryPassword);
    };

    yield put(openPopUp(POPUP_TYPES.CONFIRM, {
      titleText: i18n.t('Authentication.Password.adminTempPasswordTitle'),
      bodyText: (
        <>
          {i18n.t('Authentication.Password.adminTempPasswordBody')}
          <br />
          <span style={{ fontWeight: 900, fontSize: '22px', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
            {temporaryPassword}
          </span>
          <br />
          {deliveryBody}
        </>
      ),
      confirmText: i18n.t('Authentication.Password.adminTempPasswordCopyButton'),
      cancelText: i18n.t('Actions.Instance.close'),
      onConfirm: (confirm) => confirm && handleCopy(),
      disableBackdrop: true,
    }));

    yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_PASSWORD_RESET));
  } catch (e) {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_PASSWORD_RESET));
  } finally {
    yield put(putUserPageLoadingStatus(false));
  }
}

// Saga to handle admin MFA update
export function* callUpdateUserMfa({ payload: { userId, method } }) {
  try {
    yield put(putUserPageLoadingStatus(true));

    const result = yield adminUpdateUserMfa(userId, method);
    const blockedReason = result?.blockedReason;
    if (blockedReason === AUTH_BLOCKED_REASONS.USER_INACTIVE) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ACTIVATE_USER_FIRST));
      return;
    }
    if (blockedReason === AUTH_BLOCKED_REASONS.USER_NOT_IN_COGNITO) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_MFA_USER_NOT_IN_COGNITO));
      return;
    }
    if (blockedReason === AUTH_BLOCKED_REASONS.PHONE_NUMBER_REQUIRED) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_MFA_PHONE_REQUIRED));
      return;
    }

    if (!result?.success) {
      throw new Error('Failed to update MFA');
    }

    yield put(fetchUserPage(userId));
    yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_MFA_UPDATED));
  } catch (e) {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_MFA_UPDATED));
  } finally {
    yield put(putUserPageLoadingStatus(false));
  }
}

export function* callUpdateUserPhone({ payload: { userId, phoneNumber } }) {
  let updatedPhone;
  try {
    yield put(setPhoneUpdateLoading(true));
    const result = yield adminUpdateUserPhone(userId, phoneNumber);

    if (result?.blockedReason === AUTH_BLOCKED_REASONS.SMS_MFA_ENABLED) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_REMOVE_PHONE_SMS_MFA));
      return;
    }
    if (result?.blockedReason === AUTH_BLOCKED_REASONS.USER_NOT_IN_COGNITO) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_PHONE_USER_NOT_IN_COGNITO));
      return;
    }
    if (!result?.success) {
      throw new Error('Failed to update phone number');
    }

    updatedPhone = result.phone;
    yield put(fetchUserPage(userId));
    yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_PHONE_UPDATED));
  } catch (e) {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_PHONE_UPDATED));
  } finally {
    yield put(finishPhoneUpdate(updatedPhone));
  }
}

export function* callUpdateUserName({ payload: { userId, firstName, lastName } }) {
  try {
    yield put(putUserPageLoadingStatus(true));
    const result = yield adminUpdateUserName(userId, firstName, lastName);
    if (!result?.success) throw new Error('Failed to update user name');
    yield put(fetchUserPage(userId));
    yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_UPDATED));
  } catch (e) {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  } finally {
    yield put(putUserPageLoadingStatus(false));
  }
}

export function* callUpdateUserBirthdate({ payload: { userId, birthdate } }) {
  try {
    yield put(putUserPageLoadingStatus(true));
    const result = yield adminUpdateUserBirthdate(userId, birthdate);
    if (!result?.success) throw new Error('Failed to update user birthdate');
    yield put(fetchUserPage(userId));
    yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_UPDATED));
  } catch (e) {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  } finally {
    yield put(putUserPageLoadingStatus(false));
  }
}

function* watchCallFetchUser() {
  yield takeLatest(USER_PAGE_ACTIONS.FETCH_ONE_USER, callFetchOneUser);
}

function* watchCallAttachUserPageCourse() {
  yield takeLatest(USER_PAGE_ACTIONS.ATTACH_USER_PAGE_COURSE, callAttachUserPageCourse);
}

function* watchCallChangeUserBusinessUnits() {
  yield takeLatest(USER_PAGE_ACTIONS.CHANGE_USER_BUSINESS_UNITS, callChangeUserBusinessUnits);
}

function* watchCallAttachUserPageMentorsBulk() {
  yield takeLatest(USER_PAGE_ACTIONS.ATTACH_USER_PAGE_MENTORS_BULK, callAttachUserPageMentorsBulk);
}

function* watchCallMoveUserToCourseLevel() {
  yield takeLatest(USER_PAGE_ACTIONS.MOVE_USER_TO_COURSE_LEVEL, callMoveUserToCourseLevel);
}

function* watchCallSetUserCourseLocationFlag() {
  yield takeLatest(USER_PAGE_ACTIONS.SET_USER_COURSE_LOCATION_FLAG, callSetUserCourseLocationFlag);
}

function* watchCallClearUserCourseLocationFlag() {
  yield takeLatest(USER_PAGE_ACTIONS.CLEAR_USER_COURSE_LOCATION_FLAG, callClearUserCourseLocationFlag);
}

function* watchCallChangeUserStatus() {
  yield takeLatest(USER_PAGE_ACTIONS.CHANGE_USER_STATUS, callChangeUserStatus);
}

function* watchCallChangeUserLocale() {
  yield takeLatest(USER_PAGE_ACTIONS.CHANGE_USER_LOCALE, callChangeUserLocale);
}

function* watchCallChangeUserExperimentalFeatures() {
  yield takeLatest(USER_PAGE_ACTIONS.CHANGE_USER_EXPERIMENTAL_FEATURES, callChangeUserExperimentalFeatures);
}

function* watchCallFetchUserPersonalProgram() {
  yield takeLatest(USER_PAGE_ACTIONS.FETCH_USER_PERSONAL_PROGRAM, callFetchUserPersonalProgram);
}

function* watchCallResetUserPassword() {
  yield takeLatest(USER_PAGE_ACTIONS.RESET_USER_PASSWORD, callResetUserPassword);
}

function* watchCallUpdateUserMfa() {
  yield takeLatest(USER_PAGE_ACTIONS.UPDATE_USER_MFA, callUpdateUserMfa);
}

function* watchCallUpdateUserPhone() {
  yield takeLatest(USER_PAGE_ACTIONS.UPDATE_USER_PHONE, callUpdateUserPhone);
}

function* watchCallUpdateUserName() {
  yield takeLatest(USER_PAGE_ACTIONS.UPDATE_USER_NAME, callUpdateUserName);
}

function* watchCallUpdateUserBirthdate() {
  yield takeLatest(USER_PAGE_ACTIONS.UPDATE_USER_BIRTHDATE, callUpdateUserBirthdate);
}

export default function* userPageSaga() {
  yield all([
    fork(watchCallFetchUser),
    fork(watchCallFetchUserPersonalProgram),
    fork(watchCallAttachUserPageCourse),
    fork(watchCallChangeUserBusinessUnits),
    fork(watchCallAttachUserPageMentorsBulk),
    fork(watchCallMoveUserToCourseLevel),
    fork(watchCallSetUserCourseLocationFlag),
    fork(watchCallClearUserCourseLocationFlag),
    fork(watchCallChangeUserStatus),
    fork(watchCallChangeUserLocale),
    fork(watchCallChangeUserExperimentalFeatures),
    fork(watchCallResetUserPassword),
    fork(watchCallUpdateUserPhone),
    fork(watchCallUpdateUserName),
    fork(watchCallUpdateUserBirthdate),
    fork(watchCallUpdateUserMfa),
  ]);
}
